// E00U6EKA5EGC — dates of the 1:1 coaching. Sessions are Berlin wall-clock
// times; the API sends ISO instants, these helpers turn them back.

const TIME_ZONE = 'Europe/Berlin';

/** `2026-10-05` → `05.10.2026` */
export function formatDate(date: string | null | undefined): string {
  if (!date) {
    return '';
  }
  return date.slice(0, 10).split('-').reverse().join('.');
}

/**
 * T00TFTHNK2EY — `cc-datepicker` emits `05.10.2026`; the forms and the API
 * work with `2026-10-05`. Cleared is ''. ISO input passes through.
 */
export function toIsoDate(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const german = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(value);
  return german ? `${german[3]}-${german[2]}-${german[1]}` : value;
}

/** ISO instant → `05.10.2026, 10:00` in Berlin time */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }
  return new Intl.DateTimeFormat('de-DE', {timeZone: TIME_ZONE, dateStyle: 'medium', timeStyle: 'short'}).format(new Date(iso));
}

/** ISO instant → Berlin date (`YYYY-MM-DD`) and time (`HH:mm`) */
export function berlinParts(iso: string): {date: string; time: string} {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(iso)).map((part) => [part.type, part.value]));
  return {date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}`};
}

/** `YYYY-MM-DD` of today, shifted by the given days. */
export function isoDay(offsetDays = 0, from: Date = new Date()): string {
  const day = new Date(from.getTime() + offsetDays * 86_400_000);
  return berlinParts(day.toISOString()).date;
}

/** The call opens ten minutes before and closes 30 minutes after a session. */
export function callOpen(start: string, end: string, now: Date = new Date()): boolean {
  const opens = new Date(start).getTime() - 10 * 60_000;
  const closes = new Date(end).getTime() + 30 * 60_000;
  return now.getTime() >= opens && now.getTime() <= closes;
}

/** Whether a due date (`YYYY-MM-DD`) lies before today. */
export function isOverdue(dueDate: string | null, now: Date = new Date()): boolean {
  return !!dueDate && dueDate < isoDay(0, now);
}

/** The error code the API puts on a 4xx (`{error: 'code'}`), if any. */
export function apiErrorCode(caught: unknown): string | null {
  return (caught as {response?: {data?: {error?: string}}})?.response?.data?.error ?? null;
}

/** Only http(s) links become clickable; everything else stays text. */
const LINK = /(https?:\/\/[^\s<>"']+)/g;

/** A message split into text and http(s) links, for rendering without v-html. */
export function textParts(text: string): Array<{text: string; link: boolean}> {
  return text.split(LINK).filter((part) => part !== '').map((part) => ({text: part, link: /^https?:\/\//.test(part)}));
}
